import type { DomainTopic, TopicTier } from '../services/page-comparison';

/** Mirrors page-comparison Topic Fit card state (no component coupling). */
export interface QuickCompareTopicFitData {
  mainTopicSlug: string;
  mainTopicLabel: string;
  hasData: boolean;
  topicFound: boolean;
  tier: TopicTier | null;
  authorityScore: number | null;
  loading: boolean;
  error: string | null;
}

export function emptyTopicFitData(): QuickCompareTopicFitData {
  return {
    mainTopicSlug: '',
    mainTopicLabel: '',
    hasData: false,
    topicFound: false,
    tier: null,
    authorityScore: null,
    loading: false,
    error: null
  };
}

export function extractDomainFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return '';
  }
}

export function getTopicSignalsSorted(analysis: any): any[] {
  const signals = analysis?.topic?.topicSignals || [];
  return [...signals].sort((a, b) => (b.weight || 0) - (a.weight || 0));
}

export function getTopicSignalsCount(analysis: any): number {
  return getTopicSignalsSorted(analysis).length;
}

export function getMainTopicFromAnalysis(
  analysis: any
): { topicSlug: string; label: string; weight: number } | null {
  const signals = getTopicSignalsSorted(analysis);
  if (signals.length === 0) return null;
  const top = signals[0];
  return {
    topicSlug: top.topicSlug || '',
    label: top.label || top.topicSlug || '',
    weight: top.weight || 0
  };
}

export function computeTopicFitData(args: {
  mainTopic: { topicSlug: string; label: string; weight: number } | null;
  parentTopics: DomainTopic[] | undefined;
  domainTopicsLoading: boolean;
  domainTopicsError: string | null;
}): QuickCompareTopicFitData {
  const empty = emptyTopicFitData();
  const { mainTopic, parentTopics, domainTopicsLoading, domainTopicsError } = args;

  if (!mainTopic || !mainTopic.topicSlug) {
    return empty;
  }

  const base = {
    mainTopicSlug: mainTopic.topicSlug,
    mainTopicLabel: mainTopic.label
  };

  if (domainTopicsLoading) {
    return { ...empty, ...base, loading: true };
  }

  if (domainTopicsError) {
    return { ...empty, ...base, error: domainTopicsError };
  }

  if (parentTopics === undefined) {
    return { ...empty, ...base, loading: true };
  }

  if (parentTopics.length === 0) {
    return { ...empty, ...base, hasData: false };
  }

  const normalizedMainTopic = mainTopic.topicSlug.toLowerCase().trim();
  const matchingTopic = parentTopics.find((t) => {
    if (t.parentTopicSlug.toLowerCase().trim() === normalizedMainTopic) {
      return true;
    }
    if (
      t.childTopicSlugs?.some((child) => child.toLowerCase().trim() === normalizedMainTopic)
    ) {
      return true;
    }
    return false;
  });

  if (!matchingTopic) {
    return {
      mainTopicSlug: mainTopic.topicSlug,
      mainTopicLabel: mainTopic.label,
      hasData: true,
      topicFound: false,
      tier: null,
      authorityScore: null,
      loading: false,
      error: null
    };
  }

  return {
    mainTopicSlug: mainTopic.topicSlug,
    mainTopicLabel: matchingTopic.label || mainTopic.label,
    hasData: true,
    topicFound: true,
    tier: matchingTopic.topicTier || null,
    authorityScore: matchingTopic.authorityScore,
    loading: false,
    error: null
  };
}

export function formatTopicTier(tier: TopicTier | null): string {
  if (!tier) return 'Unknown';
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

export function getTopicTierClass(tier: TopicTier | null): string {
  if (!tier) return 'tier-unknown';
  return `tier-${tier}`;
}

export function getLinkTypeLabel(linkType: string | undefined): string {
  switch (linkType) {
    case 'contextual':
      return 'Contextual';
    case 'structural':
      return 'Structural';
    case 'same_page':
      return 'Same-page';
    case 'unknown':
    default:
      return 'Unknown';
  }
}

export function getLinkTypeTooltip(linkType: string | undefined): string {
  switch (linkType) {
    case 'contextual':
      return 'In-content link that appears on a small subset of pages.';
    case 'structural':
      return 'Navigation/footer/sidebar link that appears across many pages.';
    case 'same_page':
      return 'Anchor link within the same page (table of contents or section jump).';
    case 'unknown':
    default:
      return 'Not enough signals to classify this link yet.';
  }
}

export function getLinkTypeClass(linkType: string | undefined): string {
  switch (linkType) {
    case 'contextual':
      return 'link-type-contextual';
    case 'structural':
      return 'link-type-structural';
    case 'same_page':
      return 'link-type-same-page';
    case 'unknown':
    default:
      return 'link-type-unknown';
  }
}

export function formatAnchorText(anchorText: string | undefined): string {
  if (!anchorText || anchorText.trim().length === 0) {
    return '—';
  }
  return anchorText;
}

export function linkTargetDisplayUrl(analysis: any): string {
  return (
    analysis?.rawData?.pageData?.url || analysis?.rawData?.summary?.url || 'Current page'
  );
}

export function resolveUrlIdFromAnalysis(analysis: any): string | null {
  if (!analysis?.urlId) return null;
  if (typeof analysis.urlId === 'string') return analysis.urlId;
  const oid = analysis.urlId as any;
  return oid?._id?.toString() || oid?.toString() || null;
}
